/** Adapter between the API ad-spend record and the UI `AdSpend`.
 *  The ad-spend list endpoint returns only `clientId` (no client name), so the
 *  client name is resolved from the clients cache via `nameById`. */

import { DEFAULT_EXCHANGE_RATE } from "@/lib/constants";
import type { AdSpend, CardSource, Platform } from "@/types";

export interface ApiAdSpend {
  id: string;
  clientId: string;
  date: string;
  platform: Platform;
  source: CardSource;
  amountUsd: number | string;
  exchangeRate?: number | string | null;
  amountDzd: number | string;
  note?: string | null;
}

export interface AdSpendInput {
  clientId: string;
  clientName: string;
  platform: Platform;
  source: CardSource;
  amountUsd: number;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export function toAdSpend(api: ApiAdSpend, nameById: Map<string, string>): AdSpend {
  return {
    id: api.id,
    date: formatDate(api.date),
    clientId: api.clientId,
    clientName: nameById.get(api.clientId) ?? api.clientId,
    platform: api.platform,
    source: api.source,
    amountUsd: toNumber(api.amountUsd),
    amountDzd: toNumber(api.amountDzd),
    rate: toNumber(api.exchangeRate) || DEFAULT_EXCHANGE_RATE,
    note: api.note ?? undefined,
  };
}

/** API create body. `amountDzd` is computed server-side from amountUsd * exchangeRate. */
export function toAdSpendWriteBody(input: AdSpendInput): Record<string, unknown> {
  return {
    clientId: input.clientId,
    date: new Date().toISOString(),
    platform: input.platform,
    source: input.source,
    amountUsd: input.amountUsd,
    exchangeRate: DEFAULT_EXCHANGE_RATE,
  };
}

export function toMockAdSpend(input: AdSpendInput): AdSpend {
  return {
    id: `a_${crypto.randomUUID().slice(0, 8)}`,
    date: new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "short" }),
    clientId: input.clientId,
    clientName: input.clientName,
    platform: input.platform,
    source: input.source,
    amountUsd: input.amountUsd,
    amountDzd: Math.round(input.amountUsd * DEFAULT_EXCHANGE_RATE),
    rate: DEFAULT_EXCHANGE_RATE,
  };
}
